import {
  ageOn,
  ageToDays,
  formatAge,
  newId,
  normalizeIndianMobile,
  type PatientSnapshot,
  type Sex,
} from '@quickreport/domain';
import { type LocalDb, queueChange, type Tx } from '@quickreport/sync';

export interface PatientRow {
  id: string;
  salutation: string | null;
  name: string;
  sex: Sex | null;
  dob: string | null;
  age_days: number | null;
  age_recorded_on: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  external_id: string | null;
}

export interface PatientInput {
  id?: string;
  salutation?: string;
  name: string;
  sex?: Sex;
  /** YYYY-MM-DD */
  dob?: string;
  /** Typed age when the date of birth is not known. */
  age?: { amount: number; unit: 'Y' | 'M' | 'D' };
  phone?: string;
  email?: string;
  address?: string;
  externalId?: string;
}

// Dates come back as text (YYYY-MM-DD), not as Date objects, so age arithmetic is exact.
const COLS =
  'id, salutation, name, sex, dob::text as dob, age_days, age_recorded_on::text as age_recorded_on, phone, email, address, external_id';

/**
 * Find people by mobile number (the way the front desk asks) or name. Several people can share
 * one number, so a phone search returns everyone on it.
 */
export async function findPatients(
  db: LocalDb,
  orgId: string,
  q: string,
  limit = 12,
): Promise<PatientRow[]> {
  const text = q.trim();
  if (text.length < 2) return [];
  const digits = text.replace(/\D/g, '');
  if (digits.length >= 4 && digits.length >= text.replace(/\s/g, '').length - 2) {
    const r = await db.query<PatientRow>(
      `select ${COLS} from patients where org_id = $1 and deleted_at is null and phone like $2 order by updated_at desc limit ${limit}`,
      [orgId, `%${digits.slice(-10)}%`],
    );
    return r.rows;
  }
  const words = text.toLowerCase().split(/\s+/).filter(Boolean);
  const where = words.map((_, i) => `lower(name) like $${i + 2}`).join(' and ');
  const r = await db.query<PatientRow>(
    `select ${COLS} from patients where org_id = $1 and deleted_at is null and ${where} order by updated_at desc limit ${limit}`,
    [orgId, ...words.map((w) => `%${w}%`)],
  );
  return r.rows;
}

const day = (d: Date) => d.toISOString().slice(0, 10);

/** The person's age on a given day, from a date of birth or from the age typed when they first came. */
export function ageFor(
  p: Pick<PatientRow, 'dob' | 'age_days' | 'age_recorded_on'>,
  on: Date = new Date(),
) {
  if (p.dob) {
    const a = ageOn(p.dob, day(on));
    return { ageDays: a.totalDays, ageText: formatAge(a) };
  }
  if (p.age_days != null && p.age_recorded_on) {
    const since = Math.max(
      0,
      Math.round(
        (on.getTime() - new Date(`${p.age_recorded_on}T00:00:00Z`).getTime()) / 86_400_000,
      ),
    );
    const total = p.age_days + since;
    const years = Math.floor(total / 365.25);
    const months = Math.floor(total / 30.4375);
    return {
      ageDays: total,
      ageText: years >= 1 ? `${years} Y` : months >= 1 ? `${months} M` : `${total} D`,
    };
  }
  return { ageDays: undefined as number | undefined, ageText: '' };
}

export function snapshotOf(p: PatientRow, on: Date = new Date()): PatientSnapshot {
  const { ageDays, ageText } = ageFor(p, on);
  return {
    ...(p.salutation ? { salutation: p.salutation } : {}),
    name: p.name,
    ...(p.sex ? { sex: p.sex } : {}),
    ageText,
    ...(ageDays !== undefined ? { ageDays } : {}),
    ...(p.phone ? { phone: p.phone } : {}),
    ...(p.email ? { email: p.email } : {}),
    ...(p.address ? { address: p.address } : {}),
    ...(p.external_id ? { externalId: p.external_id } : {}),
  };
}

export class PatientError extends Error {}

/** Create or update a patient inside the caller's transaction and queue it for sync. */
export async function savePatient(
  tx: Tx,
  orgId: string,
  input: PatientInput,
  now: Date = new Date(),
): Promise<PatientRow> {
  const name = input.name.trim();
  if (!name) throw new PatientError('Enter the patient’s name.');
  let phone: string | null = null;
  if (input.phone?.trim()) {
    phone = normalizeIndianMobile(input.phone);
    if (!phone) throw new PatientError('Enter a valid 10-digit mobile number.');
  }
  if (!input.dob && !input.age) throw new PatientError('Enter the patient’s age or date of birth.');
  if (input.dob && Number.isNaN(Date.parse(input.dob)))
    throw new PatientError('That date of birth is not valid.');

  const id = input.id ?? newId();
  const ageDays = !input.dob && input.age ? ageToDays(input.age.amount, input.age.unit) : null;
  const args = [
    id,
    input.salutation || null,
    name,
    input.sex ?? null,
    input.dob ?? null,
    ageDays,
    ageDays !== null ? day(now) : null,
    phone,
    input.email?.trim() || null,
    input.address?.trim() || null,
    input.externalId?.trim() || null,
  ];
  const have = await tx.query('select 1 from patients where id = $1', [id]);
  if (have.rows[0]) {
    await tx.query(
      `update patients set salutation = $2, name = $3, sex = $4, dob = $5, age_days = $6, age_recorded_on = $7,
         phone = $8, email = $9, address = $10, external_id = $11, updated_at = now() where id = $1`,
      args,
    );
  } else {
    await tx.query(
      `insert into patients (id, org_id, salutation, name, sex, dob, age_days, age_recorded_on, phone, email, address, external_id)
       values ($1, $12, $2, $3, $4, $5, $6, $7, $8, $9, $10, $11)`,
      [...args, orgId],
    );
  }
  await queueChange(tx, 'patients', id);
  const row = await tx.query<PatientRow>(`select ${COLS} from patients where id = $1`, [id]);
  return row.rows[0] as PatientRow;
}

// ---- the Patients list ---------------------------------------------------------------

export type PatientSort = 'name' | 'added' | 'updated';

export interface PatientFilters {
  /** One patient by id. */
  id?: string;
  /** Matches name, mobile number or patient ID. */
  q?: string;
  sort?: PatientSort;
  order?: 'asc' | 'desc';
  sex?: Sex;
  page?: number;
  pageSize?: number;
}

export interface PatientListItem extends PatientRow {
  created_at: string;
  updated_at: string;
  report_count: number;
}

const PATIENT_SORT: Record<PatientSort, string> = {
  name: 'lower(p.name)',
  added: 'p.created_at',
  updated: 'p.updated_at',
};

function patientWhere(orgId: string, f: PatientFilters): { where: string; params: unknown[] } {
  const params: unknown[] = [orgId];
  const where = ['p.org_id = $1', 'p.deleted_at is null'];
  for (const w of (f.q ?? '').toLowerCase().split(/\s+/).filter(Boolean)) {
    params.push(`%${w}%`);
    const n = params.length;
    where.push(
      `(lower(p.name) like $${n} or p.phone like $${n} or lower(coalesce(p.external_id, '')) like $${n})`,
    );
  }
  if (f.id) {
    params.push(f.id);
    where.push(`p.id = $${params.length}`);
  }
  if (f.sex) {
    params.push(f.sex);
    where.push(`p.sex = $${params.length}`);
  }
  return { where: where.join(' and '), params };
}

export function patientListQuery(
  orgId: string,
  f: PatientFilters,
): { sql: string; params: unknown[] } {
  const { where, params } = patientWhere(orgId, f);
  const size = Math.min(Math.max(Math.floor(f.pageSize ?? 50), 1), 500);
  const offset = (Math.max(Math.floor(f.page ?? 1), 1) - 1) * size;
  const col = PATIENT_SORT[f.sort ?? 'name'];
  const dir =
    (f.sort ?? 'name') === 'name'
      ? f.order === 'desc'
        ? 'desc'
        : 'asc'
      : f.order === 'asc'
        ? 'asc'
        : 'desc';
  return {
    // The report count is worked out only for the rows on this page.
    sql: `select p.id, p.salutation, p.name, p.sex, p.dob::text as dob, p.age_days,
                 p.age_recorded_on::text as age_recorded_on, p.phone, p.email, p.address, p.external_id,
                 p.created_at, p.updated_at,
                 (select count(*)::int from visits v join reports r on r.visit_id = v.id
                   where v.patient_id = p.id and r.deleted_at is null) as report_count
            from patients p where ${where}
           order by ${col} ${dir}, p.id ${dir}
           limit ${size} offset ${offset}`,
    params,
  };
}

export function patientCountQuery(
  orgId: string,
  f: PatientFilters,
): { sql: string; params: unknown[] } {
  const { where, params } = patientWhere(orgId, f);
  return { sql: `select count(*)::int as n from patients p where ${where}`, params };
}

export async function getPatient(db: LocalDb, id: string): Promise<PatientRow | null> {
  const r = await db.query<PatientRow>(
    `select ${COLS} from patients where id = $1 and deleted_at is null`,
    [id],
  );
  return r.rows[0] ?? null;
}
